// MentionsProspectionTenant — onglet « Mentions de prospection » d'AdministrationTenant (T-422 / AC-7). Super admin plateforme seulement.
//
// 🔑 Lit et écrit les mentions légales d'un tenant par
//   GET /api/platform/tenants/{slug}/mentions-prospection   (textes + état de l'environnement, des BOOLÉENS : jamais une valeur secrète)
//   PUT /api/platform/tenants/{slug}/mentions-prospection
// ⛔ Tant que les quatre textes obligatoires sont vides, le serveur refuse tout envoi commercial (garde de T-306).
// ⛔ Le secret de signature des liens n'est PAS saisi ici : il se pose sur le serveur (commande remise à JJ), cet écran dit seulement s'il est posé.
// Kit commun (oh-charte) et jetons var(--oh-*) ; aucune couleur en dur.
import { useState, useEffect, useCallback } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { OhField, OhInput, OhStatusNote } from "../../../components/ui/oh-charte";

const API = process.env.REACT_APP_BACKEND_URL;
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem("session_token")}`, "Content-Type": "application/json" });

const CHAMPS = [
  { cle: "responsable_traitement", libelle: "Responsable du traitement", aide: "Nom de la société qui envoie les offres.", obligatoire: true },
  { cle: "source_donnees", libelle: "Source des coordonnées", aide: "D'où viennent l'adresse et le numéro du client.", obligatoire: true },
  { cle: "mention_email", libelle: "Mention en pied d'e-mail", aide: "Pourquoi le client reçoit ce message.", obligatoire: true },
  { cle: "mention_sms", libelle: "Mention en fin de SMS", aide: "Courte : elle s'ajoute au texte du SMS.", obligatoire: true },
  { cle: "page_desinscription", libelle: "Texte de la page de désinscription", aide: "Affiché après le clic sur le lien. Facultatif : un texte par défaut existe.", obligatoire: false },
];
const ENV = [
  ["secret_liens", "Secret de signature des liens de désinscription"],
  ["url_publique", "Adresse publique pour les liens (OMNIHUB_PUBLIC_URL)"],
  ["secret_sms", "Clé de vérification du STOP par SMS (TWILIO_SIGNATURE_SECRET)"],
];
const bouton = {
  background: "var(--oh-accent-solid)", color: "var(--oh-accent-contrast)", border: "none", borderRadius: 8, padding: "6px 12px", fontSize: 14,
};

export default function MentionsProspectionTenant({ slug }) {
  const [etat, setEtat] = useState(null);
  const [brouillon, setBrouillon] = useState({});
  const [chargement, setChargement] = useState(true);
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState(null);

  const charger = useCallback(async () => {
    setChargement(true); setErreur(null);
    try {
      const r = await fetch(`${API}/api/platform/tenants/${slug}/mentions-prospection`, { headers: auth(), credentials: "include" });
      if (!r.ok) throw new Error(r.status === 403 ? "Réservé à l'administrateur de la plateforme." : "Impossible de lire les mentions.");
      const j = await r.json();
      setEtat(j); setBrouillon(j.config);
    } catch (e) { setErreur(e.message); } finally { setChargement(false); }
  }, [slug]);
  useEffect(() => { charger(); }, [charger]);

  const enregistrer = async () => {
    setEnCours(true); setErreur(null);
    try {
      const r = await fetch(`${API}/api/platform/tenants/${slug}/mentions-prospection`, {
        method: "PUT", headers: auth(), credentials: "include", body: JSON.stringify(brouillon),
      });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || "Écriture refusée : rien n'a été modifié.");
      toast.success("Mentions de prospection enregistrées");
      await charger();
    } catch (e) { setErreur(e.message); } finally { setEnCours(false); }
  };

  if (chargement) return <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin" style={{ color: "var(--oh-text-muted)" }} /></div>;
  if (!etat) return <OhStatusNote variant="error" data-testid="mentions-erreur">{erreur || "Mentions indisponibles."}</OhStatusNote>;

  const modifie = CHAMPS.some((c) => (brouillon[c.cle] || "") !== (etat.config[c.cle] || ""));
  const manquantes = CHAMPS.filter((c) => c.obligatoire && !(brouillon[c.cle] || "").trim());
  const toutPose = ENV.every(([k]) => etat.environnement[k]);

  return (
    <div data-testid="mentions-prospection">
      <OhStatusNote variant={etat.complete ? "success" : "error"} className="mb-3" data-testid="mentions-etat">
        {etat.complete
          ? "Mentions complètes : les envois commerciaux sont possibles pour cet espace."
          : "Mentions incomplètes : tout envoi commercial est refusé pour cet espace."}
      </OhStatusNote>

      {CHAMPS.map((c) => (
        <OhField key={c.cle} label={c.libelle} htmlFor={`mentions-${c.cle}`} hint={c.aide} required={c.obligatoire} className="mb-3">
          <OhInput id={`mentions-${c.cle}`} data-testid={`mentions-${c.cle}`} maxLength={etat.limite} value={brouillon[c.cle] || ""}
                   placeholder={c.libelle} onChange={(e) => setBrouillon((p) => ({ ...p, [c.cle]: e.target.value }))} />
        </OhField>
      ))}

      {erreur && <OhStatusNote variant="error" className="mb-3" data-testid="mentions-erreur">{erreur}</OhStatusNote>}
      <button type="button" style={{ ...bouton, opacity: modifie && !manquantes.length ? 1 : 0.5 }} disabled={!modifie || manquantes.length > 0 || enCours}
              data-testid="mentions-enregistrer" onClick={enregistrer}>Enregistrer les mentions</button>
      {manquantes.length > 0 && (
        <p className="text-xs mt-2" style={{ color: "var(--oh-text-secondary)" }} data-testid="mentions-manquantes">
          À renseigner : {manquantes.map((c) => c.libelle.toLowerCase()).join(", ")}.
        </p>
      )}

      <div className="rounded-lg p-3 mt-4 text-sm" data-testid="mentions-environnement"
           style={{ background: "var(--oh-bg-elevated)", border: "0.5px solid var(--oh-border)", color: "var(--oh-text)" }}>
        <p className="mb-2" style={{ fontWeight: 500 }}>État du serveur</p>
        {ENV.map(([k, lb]) => (
          <div key={k} className="py-1 flex justify-between gap-3" style={{ borderBottom: "0.5px solid var(--oh-border)" }}>
            <span>{lb}</span>
            <span data-testid={`mentions-env-${k}`} className="px-2 rounded-lg text-xs"
                  style={{ background: etat.environnement[k] ? "var(--oh-success-bg)" : "var(--oh-error-bg)",
                           color: etat.environnement[k] ? "var(--oh-success-text)" : "var(--oh-error-text)" }}>
              {etat.environnement[k] ? "posé" : "manquant"}
            </span>
          </div>
        ))}
        {!toutPose && (
          <p className="text-xs mt-2" style={{ color: "var(--oh-text-secondary)" }}>
            Ce qui manque se pose sur le serveur par l'administrateur (le secret n'est jamais saisi ni affiché ici).
          </p>
        )}
      </div>
    </div>
  );
}
